import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import {
  type CognitoError,
  confirmForgotPassword,
  forgotPassword,
} from '../../services/cognitoService';
import './ForgotPassword.css';

type Step = 'email' | 'reset' | 'success';

interface PasswordRequirement {
  label: string;
  met: boolean;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Get email from navigation state if passed from login page
  const initialEmail = (location.state as { email?: string })?.email || '';

  // Step state
  const [step, setStep] = useState<Step>('email');
  const [destination, setDestination] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    email: initialEmail,
    code: '',
    newPassword: '',
    confirmPassword: '',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Password requirements (matching mobile) - only show met when password has content
  const newPassword = formData.newPassword;
  const passwordRequirements: PasswordRequirement[] = useMemo(
    () => [
      {
        label: t('validation.password.minLength'),
        met: newPassword.length > 0 && newPassword.length >= 12,
      },
      {
        label: t('validation.password.uppercase'),
        met: newPassword.length > 0 && /[A-Z]/.test(newPassword),
      },
      {
        label: t('validation.password.lowercase'),
        met: newPassword.length > 0 && /[a-z]/.test(newPassword),
      },
      {
        label: t('validation.password.number'),
        met: newPassword.length > 0 && /[0-9]/.test(newPassword),
      },
      {
        label: t('validation.password.specialChar', 'One special character (!@#$%...)'),
        met: newPassword.length > 0 && /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/~`]/.test(newPassword),
      },
      {
        label: t('validation.password.match'),
        met:
          newPassword.length > 0 &&
          formData.confirmPassword.length > 0 &&
          newPassword === formData.confirmPassword,
      },
    ],
    [newPassword, formData.confirmPassword, t],
  );

  // Check core requirements (excluding match for validation)
  const coreRequirementsMet =
    newPassword.length >= 12 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/~`]/.test(newPassword);

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate email step
  const validateEmailStep = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = t('validation.emailRequired');
    } else if (!isValidEmail(formData.email)) {
      errors.email = t('validation.emailInvalid');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate reset step
  const validateResetStep = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.code.trim()) {
      errors.code = t('auth.forgotPassword.codeRequired', 'Verification code is required');
    } else if (formData.code.length !== 6) {
      errors.code = t('auth.forgotPassword.codeLength', 'Code must be 6 digits');
    }

    if (!formData.newPassword) {
      errors.newPassword = t('validation.passwordRequired');
    } else if (!coreRequirementsMet) {
      errors.newPassword = t('validation.passwordRequirements');
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = t('validation.confirmPasswordRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = t('validation.passwordMismatch');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Parse and format error messages for better UX
  const formatErrorMessage = (message: string): { title: string; description: string } => {
    if (
      message.includes('not found') ||
      message.includes('UserNotFound') ||
      message.includes('User does not exist')
    ) {
      return {
        title: t('auth.forgotPassword.errors.userNotFound', 'Account Not Found'),
        description: t(
          'auth.forgotPassword.errors.userNotFoundDesc',
          'No account found with this email address. Please check your email or create a new account.',
        ),
      };
    }
    if (
      message.includes('limit') ||
      message.includes('LimitExceeded') ||
      message.includes('too many')
    ) {
      return {
        title: t('auth.forgotPassword.errors.limitExceeded', 'Too Many Attempts'),
        description: t(
          'auth.forgotPassword.errors.limitExceededDesc',
          'You have made too many requests. Please wait a few minutes and try again.',
        ),
      };
    }
    if (
      message.includes('CodeMismatch') ||
      message.includes('Invalid code') ||
      message.includes('invalid code')
    ) {
      return {
        title: t('auth.forgotPassword.errors.invalidCode', 'Invalid Code'),
        description: t(
          'auth.forgotPassword.errors.invalidCodeDesc',
          'The verification code you entered is incorrect. Please check and try again.',
        ),
      };
    }
    if (message.includes('Expired') || message.includes('expired')) {
      return {
        title: t('auth.forgotPassword.errors.expiredCode', 'Code Expired'),
        description: t(
          'auth.forgotPassword.errors.expiredCodeDesc',
          'The verification code has expired. Please request a new one.',
        ),
      };
    }
    if (message.includes('network') || message.includes('Network')) {
      return {
        title: t('auth.forgotPassword.errors.network', 'Connection Error'),
        description: t(
          'auth.forgotPassword.errors.networkDesc',
          'Please check your internet connection and try again.',
        ),
      };
    }
    return {
      title: t('auth.forgotPassword.errors.generic', 'Something Went Wrong'),
      description: message,
    };
  };

  // Handle send code
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmailStep()) return;

    setLoading(true);

    try {
      const result = await forgotPassword(formData.email.toLowerCase().trim());

      if (result.success) {
        // Use the email that was submitted
        setDestination(formData.email);
        setStep('reset');
      } else {
        // Handle specific error types
        if (result.message.includes('not found') || result.message.includes('UserNotFound')) {
          setError(
            t('auth.forgotPassword.userNotFound', 'No account found with this email address.'),
          );
        } else if (result.message.includes('limit') || result.message.includes('LimitExceeded')) {
          setError(
            t('auth.forgotPassword.limitExceeded', 'Too many attempts. Please try again later.'),
          );
        } else {
          setError(result.message);
        }
      }
    } catch (error: unknown) {
      const err = error as CognitoError;
      setError(
        err.message ||
          t('auth.forgotPassword.sendCodeFailed', 'Failed to send reset code. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle reset password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateResetStep()) return;

    setLoading(true);

    try {
      const result = await confirmForgotPassword(
        formData.email.toLowerCase().trim(),
        formData.code,
        formData.newPassword,
      );

      if (result.success) {
        setStep('success');
      } else {
        // Handle specific error types
        if (result.message.includes('CodeMismatch') || result.message.includes('Invalid code')) {
          setError(
            t(
              'auth.forgotPassword.invalidCode',
              'Invalid verification code. Please check and try again.',
            ),
          );
        } else if (result.message.includes('Expired') || result.message.includes('expired')) {
          setError(
            t('auth.forgotPassword.expiredCode', 'Code has expired. Please request a new one.'),
          );
        } else {
          setError(result.message);
        }
      }
    } catch (error: unknown) {
      const err = error as CognitoError;
      setError(
        err.message ||
          t('auth.forgotPassword.resetFailed', 'Failed to reset password. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle resend code
  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await forgotPassword(formData.email);

      if (result.success) {
        setError(''); // Clear any previous errors
        // Show success message briefly
        setValidationErrors({
          success: t('auth.forgotPassword.codeResent', 'New code sent to your email!'),
        });
        setTimeout(() => setValidationErrors({}), 3000);
      } else {
        setError(result.message);
      }
    } catch (error: unknown) {
      const err = error as CognitoError;
      setError(
        err.message ||
          t('auth.forgotPassword.resendFailed', 'Failed to resend code. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Clear general error
    if (error) {
      setError('');
    }
  };

  // Go back to login
  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="forgot-password-page">
      {/* Background - same as Landing Page */}
      <div className="bg-accent-shape" />

      <Header />

      <main className="forgot-password-main">
        {/* Hero Section */}
        <section className="forgot-password-hero">
          <div className="forgot-password-hero-content">
            <div className="hero-line"></div>
            <h2 className="hero-label">{t('auth.forgotPassword.title')}</h2>
            <div className="hero-line"></div>
          </div>
          <h1 className="hero-title">
            {step === 'success'
              ? t('auth.forgotPassword.successTitle', 'Password Reset Successfully!')
              : t('auth.forgotPassword.heroTitle', 'Reset Your Password')}
          </h1>
        </section>

        {/* Form Card */}
        <section className="forgot-password-card">
          {/* Icon Badge */}
          <div className="forgot-password-icon-badge">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <title>Lock Icon</title>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          {/* Back to login link */}
          <div className="forgot-password-login-link">
            <span>{t('auth.forgotPassword.rememberPassword', 'Remember your password?')}</span>
            <Link to="/login">{t('auth.forgotPassword.backToLogin')}</Link>
          </div>

          {/* Form Content */}
          <div className="forgot-password-form-content">
            {step !== 'success' && (
              <div className="form-header">
                <h3>
                  {step === 'email'
                    ? t('auth.forgotPassword.sendCodeTitle', 'Send Verification Code')
                    : t('auth.forgotPassword.resetTitle', 'Reset Your Password')}
                </h3>
                <p>
                  {step === 'email'
                    ? t('auth.forgotPassword.subtitle')
                    : t(
                        'auth.forgotPassword.resetSubtitle',
                        'Enter the code sent to your email and create a new password.',
                      )}
                </p>
              </div>
            )}

            {error &&
              (() => {
                const { title, description } = formatErrorMessage(error);
                return (
                  <div className="alert alert-error">
                    <span className="alert-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <title>Error Icon</title>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </span>
                    <div className="alert-content">
                      <p className="alert-title">{title}</p>
                      <p className="alert-message">{description}</p>
                    </div>
                  </div>
                );
              })()}

            {validationErrors.success && (
              <div className="alert alert-success">
                <span className="alert-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <title>Success Icon</title>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </span>
                <span>{validationErrors.success}</span>
              </div>
            )}

            {/* Step 1: Email Input */}
            {step === 'email' && (
              <form onSubmit={handleSendCode} className="forgot-password-form">
                <div className="form-group">
                  <label htmlFor="email">{t('auth.forgotPassword.emailLabel')}</label>
                  <div className="input-wrapper">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={validationErrors.email ? 'error' : ''}
                      placeholder={t('auth.forgotPassword.emailPlaceholder')}
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>
                  {validationErrors.email && (
                    <span className="error-message">{validationErrors.email}</span>
                  )}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary-action" disabled={loading}>
                    {loading
                      ? t('auth.forgotPassword.sendingCode', 'Sending...')
                      : t('auth.forgotPassword.sendCodeButton')}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Code + New Password */}
            {step === 'reset' && (
              <form onSubmit={handleResetPassword} className="forgot-password-form">
                {/* Destination info */}
                <div className="destination-info">
                  <span>{t('auth.forgotPassword.codeSentTo', 'Code sent to')}: </span>
                  <strong>{destination}</strong>
                </div>

                {/* Verification Code */}
                <div className="form-group">
                  <label htmlFor="code">
                    {t('auth.forgotPassword.codeLabel', 'Verification Code *')}
                  </label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      className={validationErrors.code ? 'error' : ''}
                      placeholder={t('auth.forgotPassword.codePlaceholder', 'Enter 6-digit code')}
                      disabled={loading}
                      maxLength={6}
                    />
                  </div>
                  {validationErrors.code && (
                    <span className="error-message">{validationErrors.code}</span>
                  )}
                </div>

                {/* New Password */}
                <div className="form-group">
                  <label htmlFor="newPassword">
                    {t('auth.forgotPassword.newPasswordLabel', 'New Password *')}
                  </label>
                  <div className="input-wrapper password-input">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className={validationErrors.newPassword ? 'error' : ''}
                      placeholder={t(
                        'auth.forgotPassword.newPasswordPlaceholder',
                        'Enter new password',
                      )}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex={-1}
                    >
                      {showNewPassword ? (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          aria-hidden="true"
                        >
                          <title>Hide Password</title>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          aria-hidden="true"
                        >
                          <title>Show Password</title>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {validationErrors.newPassword && (
                    <span className="error-message">{validationErrors.newPassword}</span>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    {t('auth.forgotPassword.confirmPasswordLabel', 'Confirm New Password *')}
                  </label>
                  <div className="input-wrapper password-input">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={validationErrors.confirmPassword ? 'error' : ''}
                      placeholder={t(
                        'auth.forgotPassword.confirmPasswordPlaceholder',
                        'Re-enter new password',
                      )}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          aria-hidden="true"
                        >
                          <title>Hide Password</title>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          aria-hidden="true"
                        >
                          <title>Show Password</title>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <span className="error-message">{validationErrors.confirmPassword}</span>
                  )}
                </div>

                {/* Password Requirements */}
                <div className="password-requirements">
                  <h4>{t('auth.register.passwordRequirementsTitle')}</h4>
                  <ul className="requirements-list">
                    {passwordRequirements.map((req) => (
                      <li key={req.label} className={req.met ? 'met' : ''}>
                        <span className="requirement-icon">{req.met ? '✓' : '○'}</span>
                        <span>{req.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary-action" disabled={loading}>
                    {loading
                      ? t('auth.forgotPassword.resetting', 'Resetting...')
                      : t('auth.forgotPassword.resetButton', 'Reset Password')}
                  </button>
                </div>

                {/* Resend code / Change email */}
                <div className="form-footer-links">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="link-button"
                    disabled={loading}
                  >
                    {t('auth.forgotPassword.resendCode', 'Resend Code')}
                  </button>
                  <span className="separator">•</span>
                  <button
                    type="button"
                    onClick={() => setStep('email')}
                    className="link-button"
                    disabled={loading}
                  >
                    {t('auth.forgotPassword.changeEmail', 'Change Email')}
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Success */}
            {step === 'success' && (
              <div className="success-content">
                <div className="success-icon">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#28a745"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <title>Success Checkmark</title>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h3>{t('auth.forgotPassword.successTitle', 'Password Reset Successfully!')}</h3>
                <p>
                  {t(
                    'auth.forgotPassword.successMessage',
                    'Your password has been reset. You can now log in with your new password.',
                  )}
                </p>
                <div className="form-actions">
                  <button type="button" className="btn-primary-action" onClick={handleBackToLogin}>
                    {t('auth.forgotPassword.goToLogin', 'Go to Login')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
